import React, { useState, useMemo, useEffect, useCallback, type ReactNode } from 'react'

import { BarcodeOutlined, DeleteOutlined, LinkOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { useOperatorData } from '@contexts/operator/OperatorDataContext'
import useBatchOperation from '@hooks/shared/useBatchOperation'
import { MultipleValue, SelectValue } from '@shared/table/Rules/component'
import TableIndex from '@shared/table/TableIndex'
import type { TableService, TableAction, SupportRule } from '@shared/table/TableIndex'
import { Tag, message, Modal, Space, Tooltip } from 'antd'
import { useTranslation } from 'react-i18next'
import i18n from 'i18next'
import { Link } from 'react-router-dom'

import GenerateShelfBarcodeModal from './GenerateShelfBarcodeModal'

interface ShelfTypeOption {
  id: string;
  name: string;
  [key: string]: unknown;
}

interface MerchantOption {
  id: string;
  name: string;
  [key: string]: unknown;
}

interface WarehouseOption {
  id: string;
  name: string;
  [key: string]: unknown;
}

interface ShelvesListService extends TableService {
  getMerchants?: () => Promise<{ success: boolean; data?: MerchantOption[]; message?: string }>;
  exportRealTimeInventory: (
    selectedMerchantIds: string[],
    locale: string
  ) => Promise<{ success: boolean; data?: { job_id: string }; message?: string }>;
  generateBarcodes: (payload: Record<string, unknown>) => Promise<unknown>;
  batchDelete: (shelfIds: string[]) => Promise<unknown>;
}

interface AddJobFn {
  (jobId: string, jobName: string, onComplete?: () => void): void;
}

interface ShelvesListProps {
  service: ShelvesListService; // New prop
  role?: 'admin' | 'operator';
  shelfTypes: ShelfTypeOption[];
  addJob?: AddJobFn;
  merchants?: MerchantOption[]; // Admin merchants can be passed as prop
  warehouses?: WarehouseOption[];
}

interface ShelfRow {
  id: string;
  location?: string;
  role?: string;
  warehouse?: { name?: string } | null;
  shelf_type?: { name?: string } | null;
  temperature?: string;
  capacity?: number | string | null;
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
}

const ShelvesList: React.FC<ShelvesListProps> = ({
  service, // New prop
  role = 'admin',
  shelfTypes,
  addJob,
  merchants = [], // Admin merchants can be passed as prop
  warehouses = [],
}) => {
  const { t } = useTranslation()
  const [lastModified, setLastModified] = useState(0)
  const [exportModalVisible, setExportModalVisible] = useState(false)
  const [adminMerchants, setAdminMerchants] = useState<MerchantOption[]>([])
  const [barcodeModalVisible, setBarcodeModalVisible] = useState(false)
  const [barcodeSelectedIds, setBarcodeSelectedIds] = useState<string[]>([])
  const [barcodeLoading, setBarcodeLoading] = useState(false)

  // 使用共用 batch operation hook
  const executeBatchOperation = useBatchOperation(service, addJob)

  // Get merchants based on role
  const operatorData = useOperatorData()
  const operatorMerchants = role === 'operator' ? (operatorData?.merchants || []) : []

  // Fetch admin merchants if not provided as prop
  useEffect(() => {
    if (role === 'admin' && merchants.length === 0 && service?.getMerchants) {
      (async () => {
        try {
          const data = await service.getMerchants!();
          if (data.success && data.data) {
            setAdminMerchants(data.data);
          }
        } catch (err) {
          console.error('Failed to fetch merchants:', err);
        }
      })();
    }
  }, [role, merchants.length, service]);


  const availableMerchants = role === 'operator' ? operatorMerchants : (merchants.length > 0 ? merchants : adminMerchants)

  // 匯出即時庫存 - 處理從 modal 確認後的回調
  const handleExportRealTimeInventory = useCallback(async (selectedMerchantIds: string[]) => {
    try {
      const result = await service.exportRealTimeInventory(selectedMerchantIds, i18n.language)

      if (result.success && result.data?.job_id) {
        message.info(t('background_job.messages.start') as string)

        if (addJob && typeof addJob === 'function') {
          addJob(
            result.data.job_id,
            t('shared.shelves.list.actions.export_real_time_inventory') as string,
            () => {
              // Export completed
            }
          )
        }
      } else {
        message.error((result.message || t('shared.shelves.list.export.error')) as string)
      }
    } catch (error) {
      console.error('[ShelvesList] Export error:', error)
      message.error(t('shared.shelves.list.export.error') as string)
    }
  }, [service, addJob, t, i18n])

  // 打開選擇貨主的 modal
  const handleOpenExportModal = useCallback(() => {
    setExportModalVisible(true)
  }, [])

  // 處理貨主選擇確認
  const handleMerchantSelectionConfirm = useCallback((selectedMerchantIds: string[]) => {
    handleExportRealTimeInventory(selectedMerchantIds)
  }, [handleExportRealTimeInventory])

  const handleGenerateBarcodes = useCallback(async (params: Record<string, unknown>) => {
    setBarcodeLoading(true)
    try {
      await executeBatchOperation({
        task: () => service.generateBarcodes({
          shelf_ids: barcodeSelectedIds,
          ...params,
        }),
        jobName: 'shared.shelves.list.actions.generate_barcode',
      })
      setBarcodeModalVisible(false)
    } catch {
      // error handled by executeBatchOperation
    } finally {
      setBarcodeLoading(false)
    }
  }, [service, barcodeSelectedIds, executeBatchOperation, t])

  const handleBatchDelete = useCallback((selectedIds: string[]) => {
    if (selectedIds.length === 0) return
    Modal.confirm({
      title: t('shared.shelves.list.batch_delete.confirm_title') as string,
      content: t('shared.shelves.list.batch_delete.confirm_body', { count: selectedIds.length }) as string,
      okText: t('shared.shelves.list.batch_delete.confirm_ok') as string,
      okType: 'danger',
      cancelText: t('common.cancel') as string,
      onOk: async () => {
        await executeBatchOperation({
          task: () => service.batchDelete(selectedIds),
          jobName: 'shared.shelves.list.actions.batch_delete',
          onSuccess: (data) => {
            setLastModified(Date.now())
            const result = (data ?? {}) as { deleted_count?: number; skipped_count?: number }
            message.success(
              t('shared.shelves.list.batch_delete.success', {
                deleted: result.deleted_count ?? 0,
                skipped: result.skipped_count ?? 0,
              }) as string,
              5
            )
          },
        })
      },
    })
  }, [service, executeBatchOperation, t])

  const columns = useMemo(() => {
    const baseColumns: Record<string, unknown>[] = [
      {
        nameKey: 'models.shelf.location',
        persist: true,
        sortable: true,
        sortFieldName: 'shelves.location',
        width: '150px',
        wrap: true,
        cell: ({ id, location }: ShelfRow) => (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Link to={`/shelves/${id}`} style={{ wordBreak: 'break-word' }}>
              {location}
            </Link>
            <a
              href={`/${role}/shelves/${id}`}
              target="_blank"
              onClick={(e) => e.stopPropagation()}
              style={{ flexShrink: 0 }} rel="noreferrer"
            >
              <LinkOutlined style={{ fontSize: '12px', color: '#999' }} />
            </a>
          </div>
        ),
      },
    ]

    baseColumns.push({
      nameKey: 'models.shelf.role',
      width: '100px',
      wrap: true,
      cell: ({ role: shelfRole }: ShelfRow) => {
        if (!shelfRole) return '-'
        const colorMap: Record<string, string> = {
          storage: 'default',
          staging: 'orange',
          carrier: 'blue'
        }
        return (
          <Tag color={colorMap[shelfRole] || 'default'}>
            {t(`enums.shelf_role.${shelfRole}`)}
          </Tag>
        )
      }
    })

    if (role === 'admin') {
      baseColumns.push({
        nameKey: 'models.warehouse.name',
        wrap: true,
        selector: ({ warehouse }: ShelfRow) => warehouse?.name || '-',
      })
    }

    baseColumns.push(
      {
        nameKey: 'models.shelf.shelf_type',
        wrap: true,
        selector: ({ shelf_type }: ShelfRow) => shelf_type?.name || '-',
      },
      {
        name: (
          <Space size={4}>
            {t('models.shelf.temperature')}
            <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{t('shared.shelves.list.column_tooltips.temperature')}</span>}>
              <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
            </Tooltip>
          </Space>
        ),
        width: '120px',
        wrap: true,
        cell: ({ temperature }: ShelfRow) => {
          if (!temperature) return '-'
          const colorMap: Record<string, string> = {
            ambient: 'default',
            controlled: 'blue',
            refrigerated: 'cyan',
            frozen: 'purple',
            air_conditioned: 'green'
          }
          return (
            <Tag color={colorMap[temperature] || 'default'}>
              {t(`enums.temperature.${temperature}`)}
            </Tag>
          )
        }
      },
      {
        name: (
          <Space size={4}>
            {t('models.shelf.capacity')}
            <Tooltip title={t('shared.shelves.list.column_tooltips.capacity') as ReactNode}>
              <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
            </Tooltip>
          </Space>
        ),
        sortable: false,
        width: '100px',
        wrap: true,
        selector: ({ capacity }: ShelfRow) => capacity || '-',
      },
      {
        nameKey: 'common.created_at',
        sortable: true,
        sortFieldName: 'shelves.id',
        width: '120px',
        wrap: true,
        selector: ({ created_at }: ShelfRow) => created_at,
      },
      {
        nameKey: 'common.updated_at',
        sortable: true,
        sortFieldName: 'shelves.updated_at',
        width: '120px',
        wrap: true,
        selector: ({ updated_at }: ShelfRow) => updated_at,
      }
    )

    return baseColumns
  }, [role, t])

  const supportRules = useMemo<SupportRule[]>(() => {
    const baseRules: SupportRule[] = [
      {
        label: t('models.shelf.location'),
        component: MultipleValue,
        inputName: 'shelves.location',
        defaultPinned: true,
        createable: true,
        tooltip: {
          title: t('shared.shelves.list.filter_tooltips.location.title'),
          content: t('shared.shelves.list.filter_tooltips.location.content'),
        },
      },
      {
        label: t('models.shelf.shelf_type'),
        component: MultipleValue,
        inputName: 'shelves.shelf_type_id',
        createable: false,
        defaultOptions: shelfTypes.map((shelf_type) => ({
          label: shelf_type.name,
          value: shelf_type.id,
        })),
        tooltip: {
          title: t('shared.shelves.list.filter_tooltips.shelf_type.title'),
          content: t('shared.shelves.list.filter_tooltips.shelf_type.content'),
        },
      },
      {
        label: t('shared.shelves.list.filters.role'),
        component: SelectValue,
        inputName: 'shelves.role',
        defaultOptions: [
          { label: t('enums.shelf_role.storage'), value: 'storage' },
          { label: t('enums.shelf_role.staging'), value: 'staging' },
          { label: t('enums.shelf_role.carrier'), value: 'carrier' },
        ],
        tooltip: {
          title: t('shared.shelves.list.filter_tooltips.role.title'),
          content: t('shared.shelves.list.filter_tooltips.role.content'),
        },
      }
    ]

    if (role === 'admin' && warehouses.length > 0) {
      baseRules.push({
        label: t('models.warehouse.name'),
        component: MultipleValue,
        inputName: 'shelves.warehouse_id',
        createable: false,
        defaultOptions: warehouses.map((w) => ({
          label: w.name,
          value: w.id,
        })),
      })
    }

    baseRules.push(
      {
        label: t('models.shelf.temperature'),
        component: MultipleValue,
        inputName: 'shelves.temperature',
        createable: false,
        defaultOptions: [
          { label: t('enums.temperature.ambient'), value: 'ambient' },
          { label: t('enums.temperature.controlled'), value: 'controlled' },
          { label: t('enums.temperature.refrigerated'), value: 'refrigerated' },
          { label: t('enums.temperature.frozen'), value: 'frozen' },
          { label: t('enums.temperature.air_conditioned'), value: 'air_conditioned' },
        ],
        tooltip: {
          title: t('shared.shelves.list.filter_tooltips.temperature.title'),
          content: t('shared.shelves.list.filter_tooltips.temperature.content'),
        },
      },
      {
        label: t('models.product.sku'),
        component: MultipleValue,
        inputName: 'product_sku',
        createable: true,
        tooltip: {
          title: t('shared.shelves.list.filter_tooltips.product_sku.title'),
          content: t('shared.shelves.list.filter_tooltips.product_sku.content'),
        },
      },
      {
        label: t('shared.shelves.list.filters.has_inventory'),
        component: SelectValue,
        inputName: 'has_inventory',
        defaultOptions: [
          { label: t('shared.shelves.list.filters.has_inventory_true'), value: 'true' },
          { label: t('shared.shelves.list.filters.has_inventory_false'), value: 'false' },
        ],
        tooltip: {
          title: t('shared.shelves.list.filter_tooltips.has_inventory.title'),
          content: t('shared.shelves.list.filter_tooltips.has_inventory.content'),
        },
      },
    )


    return baseRules
  }, [t, role, warehouses])

  const tableActions = useMemo<TableAction[]>(() => {
    if (role !== 'operator') return []

    return [
      {
        name: 'shared.shelves.list.actions.generate_barcode',
        icon: <BarcodeOutlined />,
        do: (selected) => {
          const selectedIds = Array.from(selected) as string[]
          setBarcodeSelectedIds(selectedIds)
          setBarcodeModalVisible(true)
        },
        persist: false,
        type: 'default',
      },
      {
        name: 'shared.shelves.list.actions.batch_delete',
        icon: <DeleteOutlined />,
        do: (selected) => {
          handleBatchDelete(Array.from(selected) as string[])
        },
        persist: false,
        danger: true,
      },
    ]
  }, [role, handleBatchDelete])

  return (
    <>
      <TableIndex
        columns={columns}
        searchable={{
          match: 'prefix',
          searchFields: [
            t('models.shelf.location'),
          ],
        }}
        columnsEditable={true}
        tableId={`${role.charAt(0).toUpperCase() + role.slice(1)}ShelvesTable`}
        defaultQuery={{
          sorting_field: 'shelves.id',
          sorting_direction: 'desc',
        }}
        supportRules={supportRules}
        lastModified={lastModified}
        setLastModified={setLastModified}
        service={service}
        actions={tableActions}
        filterHelpExample={t('shared.shelves.list.filter_help_example') as string}
        filterDocsUrl={`/docs/${role}/shelves/list#features`}
      />
      {role === 'operator' && (
        <GenerateShelfBarcodeModal
          visible={barcodeModalVisible}
          selectedCount={barcodeSelectedIds.length}
          onCancel={() => {
            setBarcodeModalVisible(false)
            setBarcodeLoading(false)
          }}
          onSubmit={handleGenerateBarcodes}
          loading={barcodeLoading}
        />
      )}
    </>
  )
}

export default ShelvesList
